import accountService from '../services/accountService'
import {externalScriptsService, storageService, staffService, tagsService} from '@/modules/common/services'
import {urlUtil} from '@/modules/common/utils'
import {frontageApiService} from "@/plugins/frontage-api";
import {BusinessDateFormats} from '@/modules/common/filters'
import matterTermService from "@/modules/matters/services/matterTermService"
import {SmsSettingsService} from "@/modules/settings/services/SmsSettingsService";
import {settingsService} from "../../settings/services/settingsService";
import Vue from 'vue';

const state = {
  profile: null,
  mark_as_read: false,
  email_cc: false,
  accountLoaded: false,
  loading: true,
  workAsStaff: null,
  teamsViewFilter: null,
  dateTimeFormats: null,
  staffMembers: null,
  activeDedicatedNumber: null,
  activeDedicatedTextingBusinessPhoneNumber: null,
  isTwilioActive: false,
  integrationsSettings: {},
  businessSettings: null,
  businessTerm: {},
  isSmsEnabled: false,
  tags: [],
}

const getters = {
  account: state => state.profile,
  accountLoaded: state => !!state.profile,
  loading: state => state.loading,
  business: state => state.profile && state.profile.business,
  settings: state => state.profile && state.profile.settings,
  profile: state => state.profile,
  mark_as_read: state => state.mark_as_read,
  email_cc: state => state.email_cc,
  isSmsEnabled: state => state.isSmsEnabled,
  staff: state => state.workAsStaff,
  fields: state => state.profile && state.profile.client_fields,
  isStar: state => {
    const business = state.profile ? state.profile.business : null
    if (!business) return false
    const {whitelabel_configuration_key} = business
    return whitelabel_configuration_key && whitelabel_configuration_key.toLowerCase() === 'star'
  },
  isSpecialDirectory: state => {
    if (state.profile)
      return state.profile.whitelabel || (state.profile.business && state.profile.business.managed_co_branded)
    return false
  },
  hasAccessToSettings: state => {
    if (state.profile && state.profile.staff) {
      return state.profile.staff.permissions['can_access_settings']
    } else {
      return state.profile.is_owner
    }
  },
  isOwner: state => state.profile?.is_owner,
  isAdmin: state => {
    if (state.profile && state.profile.staff) {
      return state.profile.staff.role === 'admin'
    } else {
      return state.profile.is_owner
    }
  },
  isCollaborator: state => {
    if (state.workAsStaff) {
      return state.workAsStaff.role === 'admin' || state.workAsStaff.role === 'manager' || state.workAsStaff.role === 'collaborator'
    } else {
      return state.profile && state.profile.is_owner
    }
  },
  teamsViewFilter: state => state.teamsViewFilter,
  hasPermission:
    state =>
      name =>
        state.workAsStaff &&
        state.workAsStaff.permissions[name] &&
        state.profile.staff &&
        state.profile.staff.permissions[name],
  isMultipleStaffs: (state, getters) => getters.business && getters.business.multistaff,
  pendo: state => state.profile && state.profile.external_integrations && state.profile.external_integrations.pendo,
  dateFormats: state => state.dateTimeFormats.date,
  timeFormats: state => state.dateTimeFormats.time,
  businessTerm: state => state.businessTerm,
  staffMembers: state => state.staffMembers,
  isExpired: state => state.profile.notifications.account_blocked,
  activeDedicatedNumber: state => state.activeDedicatedNumber,
  isTwilioActive: state => state.profile.settings.twilio_active,
  isMultiStaff: state => state.staffMembers.filter(member => member.active).length > 1,
  integrationsSettings: state => state.integrationsSettings,
  hasPromotionalSmsEnabled: (state, getters, rootState, rootGetters) => {
    if (!rootGetters.hasFeature?.('rollout.communication.sms_campaigns_in_texting_app')) return false;
    const number = state.activeDedicatedTextingBusinessPhoneNumber;
    if (!number) return false;
    return number.features?.includes('SMS') && number.verification_request_data?.campaign_sms === true;
  },
  isCampaignSmsRegistrationApproved: (state, getters, rootState, rootGetters) => {
    if (!rootGetters.hasFeature?.('rollout.communication.sms_campaigns_in_texting_app')) return false;
    if (!rootGetters.hasFeature?.('rollout.communication.sms_campaigns_from_shared_numbers.deny')) return false;
    const number = state.activeDedicatedTextingBusinessPhoneNumber;
    if (!number) return false;
    return number.promotional_verification_status === 'verified' &&
      number.verification_request_data?.campaign_sms === true;
  },
  isStrictCompliantFlow: (state) => state.profile?.settings?.is_strict_einvoicing ?? false,
  businessSettings: state => state.businessSettings,
  isOnlinePaymentsEnabled: state => state.profile && state.profile.settings && state.profile.settings.payments_enabled,
  isAmPmTimeFormat: state => state.profile?.business?.time_format === '12h',
  firstDayOfWeek: state => state.profile?.settings?.first_day,
  staffTimeZoneOffset: state => state.workAsStaff.time_zone_offset,
  staffTimeZoneName: state => state.workAsStaff.time_zone_name,
  display_country_code: state => state.profile?.settings?.display_country_code,
  calendarInterval: (state) => {
    const interval = state.staffMembers.find(member => member.uid === state.profile.staff.uid)?.calendar_settings?.slot_intervals

    if (interval === 20) {
      return 15
    }
    return Math.min(interval, 60)
  },
  staffUid: state => state.workAsStaff.uid,
  tags: state => state.tags,
  activeStaffMembers: state => state.staffMembers.filter(member => member.active),
  currency: state => state.profile.settings.currency,

}

const mutations = {
  setProfile(state, profile) {
    state.profile = profile
  },
  setLoading(state, value) {
    state.loading = value
  },
  setWorkAsStaff(state, value) {
    state.workAsStaff = value
  },
  setTeamsViewFilter(state, teamsViewFilter) {
    state.teamsViewFilter = teamsViewFilter
  },
  setDateTimeFormats(state, dateTimeFormats) {
    state.dateTimeFormats = dateTimeFormats
  },
  setIsSmsEnabled(state, isSmsEnabled) {
    state.isSmsEnabled = isSmsEnabled
  },
  setBusinessTerm(state, value) {
    state.businessTerm = matterTermService.buildTerm(value)
  },
  setStaffMembers(state, value) {
    state.staffMembers = value
  },
  setActiveDedicatedNumber(state, activeDedicatedNumber) {
    state.activeDedicatedNumber = activeDedicatedNumber
  },
  setActiveDedicatedTextingBusinessPhoneNumber(state, activeDedicatedTextingBusinessPhoneNumber) {
    state.activeDedicatedTextingBusinessPhoneNumber = activeDedicatedTextingBusinessPhoneNumber
  },
  setIntegrationsSettings(state, integrationsSettings) {
    state.integrationsSettings = integrationsSettings
  },
  setBusinessSettings(state, businessSettings) {
    state.businessSettings = businessSettings
  },
  setMarkAsRead(state, markAsRead) {
    Vue.set(state, 'mark_as_read', markAsRead);
  },
  setEmailCc(state, emailCc) {
    Vue.set(state, 'email_cc', emailCc);
  },
  setStaffAttributes(state, Attributes) {
    Object.assign(state.profile.staff, Attributes)
  },
  setIsOnlinePaymentsEnabled(state, payments_enabled) {
    state.profile.settings.payments_enabled = payments_enabled
  },
  setTags(state, tags) {
    state.tags = tags;
  },
  setStaffTimeZone(state, {timeZone, timeZoneName}) {
    state.workAsStaff.time_zone = timeZone
    state.workAsStaff.time_zone_name = timeZoneName
  },
}

const actions = {
  async checkIsSmsEnabled({dispatch, commit, state, rootGetters}) {
    await dispatch('appMarketModule/loadApps', {}, { root: true });

    await Promise.all([
      dispatch('appMarketModule/checkAppSubscription', 'callsandtexting', { root: true }),
      dispatch('appMarketModule/checkAppSubscription', 'texting', { root: true }),
    ]);

    const voiceTextApp = rootGetters['appMarketModule/app']('callsandtexting');
    const voiceTextAppHasActiveSubscription = rootGetters['appMarketModule/hasActiveSubscriptionToApp']('callsandtexting');

    const textingApp = rootGetters['appMarketModule/app']('texting');
    const textingHasActiveSubscription = rootGetters['appMarketModule/hasActiveSubscriptionToApp']('texting');

    const hasTextApp = (voiceTextApp || voiceTextAppHasActiveSubscription || textingApp || textingHasActiveSubscription);
    const activeDedicatedNumber = state.activeDedicatedNumber;
    const twoWayTextingEnabled = !!(hasTextApp || activeDedicatedNumber);

    const hasByeZipwipFF = rootGetters.hasFeature('bye_zipwip');
    const isTwilioActive = state.profile.settings.twilio_active;
    const twilioEnabled = !!(hasByeZipwipFF || isTwilioActive)

    commit('setIsSmsEnabled', twoWayTextingEnabled || twilioEnabled)
  },
  setLoading({commit}, value) {
    commit('setLoading', value)
  },
  async fetchAccount({commit, dispatch, getters, rootGetters}, retryAfterException = true) {
    try {
      const staffUid = urlUtil.getParameterByName("staffUid");
      commit('setLoading', true)
      let account = storageService.get(`account_${staffUid}`)

      if (!account || !account.profile || !account.workAsStaff) {
        const profile = await accountService.getAccount({full: true})
        const teams_view_filter = rootGetters.isMobileApp ? urlUtil.getParameterByName("teamsViewFilter") : await frontageApiService.get('teams_view_filter');
        account = {profile, workAsStaff: profile.staff, teams_view_filter}
      }

      const {profile, workAsStaff, teams_view_filter} = account
      commit('setProfile', profile)
      commit('setWorkAsStaff', workAsStaff)
      commit('setMarkAsRead', profile.settings.mark_as_read)
      commit('setEmailCc', profile.business.email_cc)
      commit('setTeamsViewFilter', teams_view_filter)

      if (profile.constants.date_time_formats) {
        let date_time_formats = JSON.parse(profile.constants.date_time_formats)
        date_time_formats.date = JSON.parse(date_time_formats.date)
        date_time_formats.time = JSON.parse(date_time_formats.time)
        BusinessDateFormats.init(date_time_formats, profile.business.time_format)
        commit('setDateTimeFormats', date_time_formats)
      }

      commit({type: 'setFeatures', features: profile.business.features}, {root: true})

      dispatch({type: 'brandModule/initBrand', brand: profile.brand}, {root: true})

      commit('setBusinessTerm', profile.settings.selected_term);

      let {staff, error} = await staffService.getStaff();
      let count = 0;
      while(error && count++ < 10) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        ({staff, error} = await staffService.getStaff());
      }
      if(!error) {
        commit('setStaffMembers', staff);
      }


      commit('setLoading', false)
      externalScriptsService.set(getters.account, {
          isStar: getters.isStar,
          isMobile: rootGetters.isMobile,
          hasFeature: rootGetters.hasFeature,
          isMobileApp: rootGetters.isMobileApp
        },
        {
          matterTerm: getters.businessTerm?.term,
          numOfStaff: getters.staffMembers.filter(member => member.active).length
        }
      )
      dispatch({type: 'accountModule/fetchIntegrationsSettings'}, {root: true})
      dispatch({type: 'checkIsSmsEnabled'})
      if(error) {
        throw new Error(error);
      }
    } catch (e) {
      console.error(e)
      commit('setLoading', false)
      if(retryAfterException) {
        dispatch('fetchAccount', false)
      }
    }
  },
  async fetchActiveDedicatedNumber({commit, dispatch}) {
    const activeDedicatedNumber = await SmsSettingsService.getBusinessActiveDedicatedNumber()
    commit('setActiveDedicatedNumber', activeDedicatedNumber)
    dispatch({type: 'checkIsSmsEnabled'})
  },
  async fetchActiveDedicatedTextingBusinessPhoneNumber({commit}) {
    const activeDedicatedTextingBusinessPhoneNumber = await SmsSettingsService.getActiveDedicatedTextingBusinessPhoneNumber()
    commit('setActiveDedicatedTextingBusinessPhoneNumber', activeDedicatedTextingBusinessPhoneNumber)
  },
  async fetchIntegrationsSettings({commit}) {
    const integrationsSettings = await settingsService.getIntegrationsSettings()
    commit('setIntegrationsSettings', integrationsSettings)
  },
  async fetchBusinessSettings({state, commit}, {payload}) {
    const {businessId, forceFetch = false} = payload
    //AVOID duplicate server calls
    if (state.businessSettings && !forceFetch) return
    try {
      let settings = await settingsService.getBusinessSettings(businessId)
      commit('setBusinessSettings', settings)
    } catch (e) {
      console.log("fetchBusinessSettings", e)
    }
  },
  async updateInboxNotificationSettings({commit, state}, inboxNotificationSettings) {
    try {
      if (inboxNotificationSettings) {
        let res = await settingsService.updateInboxNotificationSettings(inboxNotificationSettings)
        if (res) {
          commit('setMarkAsRead', inboxNotificationSettings.settings.mark_as_read)
          commit('setEmailCc', inboxNotificationSettings.business.email_cc)
          commit('setProfile', state.profile)
          return res
        }
      }
    } catch(e) {
      console.error('error:', e.error)
    }
  },
  async updateOnlinePayments({commit}, {payments_enabled}) {
    try {
      await settingsService.update({payments_enabled})
      commit('setIsOnlinePaymentsEnabled', payments_enabled)
    } catch (e) {
      console.log("updateOnlinePayments", e)
    }
  },
  updateStaffAttributes({commit}, {payload}) {
    commit('setStaffAttributes', payload)
  },
  async reloadStaff({commit}) {
    const {staff} = await staffService.getStaff();
    commit('setStaffMembers', staff)
  },
  async setStaffActiveStatus({commit, getters}, {staffUid, isActive}) {
    try {
      await staffService.setStaffActiveStatus({staffUid, isActive})
      const {staff} = await staffService.getStaff();
      commit('setStaffMembers', staff)
    } catch (e) {
      console.error("setStaffActiveStatus", e)
    }
  },
  async updateStaff({commit, getters}, payload) {
    try {
      await staffService.updateStaff({staffUid: payload.staffUid || getters.staff.uid}, payload)
      const {staff} = await staffService.getStaff();
      const currentStaff = staff.find(member => member.uid === payload.staffUid)
      commit('setLoading', true)
      commit('setStaffTimeZone', {timeZone: currentStaff.time_zone, timeZoneName: currentStaff.time_zone_name})
      setTimeout(() => commit('setLoading', false), 200)
      commit('setStaffMembers', staff)
    } catch (e) {
      console.error("updateStaff", e)
    }
  },
  async getTags({commit}) {
    const {tags} = await tagsService.getTags();
    tags && commit('setTags', tags);
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
